import {
  MessageType,
  PublicEWebinarFragment,
  MessageSubType,
  SessionType,
  ChatType,
} from '$/GraphQL/schema/types';
import { PlayerMode } from '../WebinarPlayer/types';
import { MessageInList } from './types';

export abstract class ChatMessages {
  public constructor(public messages: MessageInList[] = []) {}

  // get time in room seconds of last attendee message
  public getTIRCLastAttendeeMessage(): number | undefined {
    let tirc = undefined;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].fromAttendee) {
        tirc = this.messages[i].timeInWebinarSecs;
        break;
      }
    }
    return tirc;
  }

  // get first moderator message (attendee view)
  public getFirstModeratorMessage(): MessageInList | null {
    let firstModeratorMessage: MessageInList | null = null;

    for (let i = 0; i < this.messages.length; i++) {
      if (!this.messages[i].fromAttendee) {
        firstModeratorMessage = this.messages[i];
        break;
      }
    }
    return firstModeratorMessage;
  }

  public getFirstAttendeeMessage(): MessageInList | null {
    // First attendee message after Automatic messages (EmailCapture, NameCapture)
    let messages = [...this.messages];
    let lastAutomaticMsgIndex;
    const automaticTypes = [
      MessageSubType.EmailCapture,
      MessageSubType.NameCapture,
      MessageSubType.EmailCaptureError,
    ];
    for (let i = 0; i < messages.length; i++) {
      if (!messages[i].subtype || messages[i].type !== MessageType.Automatic) continue;
      if (automaticTypes.includes(messages[i].subtype as MessageSubType)) {
        lastAutomaticMsgIndex = i;
      }
    }
    if (lastAutomaticMsgIndex) {
      messages = messages.slice(lastAutomaticMsgIndex);
    }

    let firstAttendeeMessage: MessageInList | null = null;
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].fromAttendee && messages[i].type === MessageType.Chat) {
        firstAttendeeMessage = messages[i];
        break;
      }
    }
    return firstAttendeeMessage;
  }

  public getFirstUnresponsedAttendeeMessage(): MessageInList | null {
    // Find last moderator Chat message
    let lastModeratorMessage: MessageInList | null = null;
    let i = this.messages.length - 1;
    while (i >= 0 && !lastModeratorMessage) {
      if (!this.messages[i].fromAttendee && this.messages[i].type === MessageType.Chat) {
        lastModeratorMessage = this.messages[i];
        break;
      }
      i--;
    }

    // first attendee message after the last response from the moderator
    let firstUnrespondedAttendeeMessage: MessageInList | null = null;
    while (++i < this.messages.length && !firstUnrespondedAttendeeMessage) {
      if (this.messages[i].fromAttendee && this.messages[i].type === MessageType.Chat) {
        firstUnrespondedAttendeeMessage = this.messages[i];
      }
    }

    return firstUnrespondedAttendeeMessage;
  }

  // get last attendee message (attendee view)
  public getLastAttendeeChatMessage(): MessageInList | null {
    let lastMessage: MessageInList | null = null;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].fromAttendee && this.messages[i].type === MessageType.Chat) {
        lastMessage = this.messages[i];
        break;
      }
    }
    return lastMessage;
  }

  public getLastModeratorChatMessage(): MessageInList | null {
    let lastMessage: MessageInList | null = null;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (!this.messages[i].fromAttendee && this.messages[i].type === MessageType.Chat) {
        lastMessage = this.messages[i];
        break;
      }
    }
    return lastMessage;
  }

  // find AutoResponder Message type
  public hasAutoResponderType(): boolean {
    return this.messages.some((message) => {
      return message.type === MessageType.AutoResponder;
    });
  }

  // STATIC

  public static isChatDisabled(
    mode: PlayerMode,
    ewebinar: PublicEWebinarFragment,
    sessionType?: SessionType | null
  ): boolean {
    if (mode === PlayerMode.replay || !sessionType) {
      return ewebinar.chatSettings[SessionType.Replay].type !== ChatType.Chat;
    }
    return ewebinar.chatSettings[sessionType].type !== ChatType.Chat;
  }
}

export class AttendeeChatMessages extends ChatMessages {}
export class ModeratorChatMessages extends ChatMessages {}
